import type {
  IOnboardingImage,
  IFeaturedImagesResponse,
  IOnboardingImagesSearchQuery,
  IUpdateOnboardingImage,
  IUpdateOnboardingOrderBody,
  IOnboardingImageResponse,
  IOnsearchImagesResponse,
} from '@/types/onboarding.types';
import { AxiosError } from 'axios';
import axiosInstance from '@/services/interceptor.services';
import {
  idFetauredUpta,
  mediaId,
  storeCommerceId,
  storeCommerceSlug,
} from '@/refs/commerce.refs';
import { toRaw } from 'vue';
import { restaurantId } from '@/refs/common.refs';
import { query } from '@/refs/onboarding.refs';

export const onboardingCommerceImage = {
  /**
   * Crée une nouvelle image d'onboarding pour un restaurant.
   * @param data - Informations de l'image d'onboarding (IOnboardingImage)
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  createOnboardingCommerceImage: async (data: IOnboardingImage) => {
    try {
      const formData = new FormData();
      formData.append('restaurantId', data.restaurantId);
      formData.append('role', data.role);
      formData.append('displayOrder', String(data.displayOrder));

      if (data.startDate) formData.append('startDate', data.startDate);
      if (data.endDate) formData.append('endDate', data.endDate);
      formData.append('isActive', String(data.isActive ?? true));
      if (data.metadata) {
        if (data.metadata.title)
          formData.append('metadata[title]', data.metadata.title);
        if (data.metadata.description)
          formData.append('metadata[description]', data.metadata.description);
        if (data.metadata.slideDuration)
          formData.append(
            'metadata[slideDuration]',
            String(data.metadata.slideDuration),
          );
        formData.append(
          'metadata[skipAllowed]',
          String(data.metadata.skipAllowed ?? true),
        );
      }

      if (data.file) {
        formData.append('file', data.file);
      }

      const response = await axiosInstance.post(
        `restaurants/onboarding-images/add`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère la liste des images “featured” pour un commerce donné.
   *
   * Cette fonction utilise soit l'identifiant du commerce (`storeCommerceId`)
   * soit le slug du commerce (`storeCommerceSlug`) pour effectuer la requête.
   *
   * @async
   * @function getCommercesFeaturedImages
   * @returns {Promise<IFeaturedImagesResponse>} Promise contenant la réponse typée IFeaturedImagesResponse ou rejetée avec une erreur Axios.
   */
  getCommercesFeaturedImages: async (): Promise<IFeaturedImagesResponse> => {
    try {
      const paramsId = Number(toRaw(storeCommerceId.value));
      const storeSlugId = Number(toRaw(storeCommerceSlug.value));

      const response = await axiosInstance.get<IFeaturedImagesResponse>(
        `restaurants/featured-images/get-by-store/${paramsId ?? storeSlugId}`,
      );

      return response.data; // Retourne directement les données typées
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Recherche les images d'onboarding d’un commerce avec filtres, tri et pagination.
   *
   * Cette fonction permet :
   * - le tri (created, displayOrder, role)
   * - la pagination
   * - les filtres (isActive, date, restaurant, etc.)
   *
   * @async
   * @function getSearchCommercesOnboardingImages
   * @param {IOnboardingImagesSearchQuery} query - Paramètres de recherche
   * @returns {Promise<IFeaturedImagesResponse>} Résultat de la recherche des images d'onboarding
   */
  getSearchCommercesOnboardingImages:
    async (): Promise<IOnsearchImagesResponse> => {
      try {
        const params = new URLSearchParams();

        params.append('sortBy', query.value.sortBy ?? 'created');
        params.append('sortOrder', query.value.sortOrder ?? 'DESC');
        params.append('pagination', String(query.value.pagination ?? true));
        params.append('page', String(query.value.page ?? 1));
        params.append('pageSize', String(query.value.pageSize ?? 20));

        if (query.value.q) {
          params.append('q', query.value.q);
        }

        if (query.value.displayOrder !== undefined) {
          params.append('displayOrder', String(query.value.displayOrder));
        }

        if (query.value.isActive !== undefined) {
          params.append('isActive', String(query.value.isActive));
        }

        const resolvedRestaurantId =
          query.value.restaurantId || restaurantId.value;
        const uuidRegex =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (
          resolvedRestaurantId &&
          uuidRegex.test(String(resolvedRestaurantId))
        ) {
          params.append('restaurantId', String(resolvedRestaurantId));
        }

        const response = await axiosInstance.get<IOnsearchImagesResponse>(
          `restaurants/onboarding-images/search?${params.toString()}`,
        );

        return response.data;
      } catch (error) {
        const err = error as AxiosError;
        return Promise.reject(err);
      }
    },

  /**
   * Met à jour une image d'onboarding existante.
   *
   * @async
   * @function updateOnboardingImage
   * @param {IUpdateOnboardingImage} data - Données de mise à jour de l'image
   * @returns {Promise<any>} Réponse de l'API ou erreur Axios
   */
  updateOnboardingImage: async (data: any) => {
    try {
      const storeId = String(toRaw(idFetauredUpta.value));

      const response = await axiosInstance.put(
        `restaurants/onboarding-images/update/${storeId}`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );

      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Met à jour l'ordre d'affichage des images d'onboarding d'un commerce.
   *
   * @async
   * @function updateOnboardingImagesOrder
   * @param {string} storeId - L'id du commerce
   * @param {IUpdateOnboardingOrderBody} body - Données d'ordre d'affichage
   * @returns {Promise<any>} Réponse de l'API ou erreur Axios
   */
  updateOnboardingImagesOrder: async (body: IUpdateOnboardingOrderBody) => {
    try {
      const storeId = toRaw(storeCommerceId.value);
      const response = await axiosInstance.put(
        `restaurants/onboarding-images/set-order/${storeId}`,
        body,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );

      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime toutes les images d'onboarding d'un commerce.
   *
   * @async
   * @function deleteAllOnboardingImages
   * @param {string} storeId - L'id du commerce
   * @returns {Promise<any>} Réponse de l'API ou erreur Axios
   */
  deleteAllOnboardingImages: async () => {
    try {
      const storeId = Number(toRaw(storeCommerceId.value));
      const response = await axiosInstance.delete(
        `restaurants/onboarding-images/delete-all/${storeId}`,
      );

      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime une image “featured” par son identifiant.
   *
   * @async
   * @function deleteFeaturedImageById
   * @param {string} mediaId - L'id du media à supprimer
   * @returns {Promise<any>} Réponse de l'API ou erreur Axios
   */
  deleteFeaturedImageById: async () => {
    try {
      const media = toRaw(mediaId.value);
      const response = await axiosInstance.delete(
        `restaurants/onboarding-images/delete/${media}`,
      );

      return response.data;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
};
